/**
 * AssetRegistry - the "no asset ever blocks progress" mechanism.
 *
 * Every asset id resolves to SOMETHING: a real GLB when one has been generated
 * and imported, otherwise a procedural primitive built from the manifest's
 * `placeholder` block. The game is fully playable, tunable and shippable with
 * every entry at status:placeholder.
 *
 * An asset agent's entire job is: generate -> `npm run import-asset` -> set
 * `model` -> flip `status` to `ready`. It NEVER edits code. If you find yourself
 * wanting to special-case an asset in a .ts file, put it in the manifest instead.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import manifest from '@data/assets.manifest.json';
import { warnOnce } from '@/core/assert';
import { applyToonShading, makeToonMaterial } from '@/render/toonMaterial';
import { validateAssetRig, type AssetRigDef } from '@/engine/assetRig';

export type PlaceholderShape = 'box' | 'cylinder' | 'sphere' | 'cone' | 'capsule';

export interface PlaceholderDef {
  shape: PlaceholderShape;
  /** Bounding box [width, height, depth] with ORIGIN AT THE BASE. */
  size: [number, number, number];
  color: string;
}

export interface PhysicsDef {
  mass: number;
  material: string;
  breakable: boolean;
  breakForce?: number;
}

export interface AssetDef {
  id: string;
  status: 'placeholder' | 'generating' | 'review' | 'ready';
  placeholder: PlaceholderDef;
  physics: PhysicsDef;
  model: string | null;
  provider?: string;
  prompt?: string;
  notes?: string;
  attachPoints?: Record<string, [number, number, number]>;
  rig?: AssetRigDef;
}

const defs = new Map<string, AssetDef>(
  (manifest.assets as unknown as AssetDef[]).map((a) => [a.id, a]),
);

/** Loaded GLB scenes, keyed by asset id. Cloned per instance. */
const loadedModels = new Map<string, THREE.Object3D>();
const loader = new GLTFLoader();

export function getAssetDef(id: string): AssetDef | undefined {
  return defs.get(id);
}

export function allAssetDefs(): AssetDef[] {
  return [...defs.values()];
}

export const styleContract: string = manifest.styleContract;

/**
 * Physics properties for an asset. Falls back to a light, unbreakable default
 * so an unknown id degrades into something inert rather than crashing a level.
 */
export function getPhysics(id: string): PhysicsDef {
  const def = defs.get(id);
  if (!def) {
    warnOnce(`asset:${id}`, `Unknown asset "${id}" - add it to data/assets.manifest.json`);
    return { mass: 0.5, material: 'unknown', breakable: false };
  }
  return def.physics;
}

/** Bounding size [w, h, d] of an asset, used for collider shapes and placement. */
export function getSize(id: string): [number, number, number] {
  return defs.get(id)?.placeholder.size ?? [0.2, 0.2, 0.2];
}

/**
 * Preloads every asset whose status is 'ready'. Assets still on placeholders
 * cost nothing to "load", which is why a fresh clone of this repo boots
 * instantly with no asset downloads at all.
 */
export async function preloadReadyAssets(basePath = ''): Promise<void> {
  const ready = allAssetDefs().filter((def) => def.status === 'ready' && def.model);
  await Promise.all(
    ready.map(async (def) => {
      loadedModels.delete(def.id);
      try {
        const gltf = await loader.loadAsync(`${basePath}${def.model as string}`);
        const problems = registerLoadedModel(def, gltf.scene);
        if (problems.length > 0) {
          warnOnce(
            `asset-rig:${def.id}`,
            `Invalid rig for "${def.id}": ${problems.join('; ')}. Using placeholder.`,
          );
        }
      } catch (error) {
        // A failed load must not break the level - fall back to the placeholder.
        console.warn(
          `[cat-game] failed to load "${def.id}" from ${def.model}, using placeholder`,
          error,
        );
      }
    }),
  );
}

/**
 * Builds a fresh Object3D for an asset id.
 * Returns a clone of the loaded GLB when available, otherwise a primitive.
 * Callers own the returned object and may reparent/dispose it freely.
 */
export function createAssetObject(id: string): THREE.Object3D {
  const loaded = loadedModels.get(id);
  if (loaded) return cloneSkeleton(loaded);

  const def = defs.get(id);
  if (!def) {
    warnOnce(`asset:${id}`, `Unknown asset "${id}" - rendering a magenta error box`);
    return errorBox();
  }
  return buildPlaceholder(def);
}

/** Explicit fallback for an actor that rejects a loaded model's presentation contract. */
export function createPlaceholderObject(id: string): THREE.Object3D {
  const def = defs.get(id);
  if (!def) return errorBox();
  return buildPlaceholder(def);
}

/** True when a real model is in use. The debug HUD shows the placeholder count. */
export function isPlaceholder(id: string): boolean {
  return !loadedModels.has(id);
}

export function placeholderCount(): number {
  return allAssetDefs().filter((def) => !loadedModels.has(def.id)).length;
}

/* ------------------------------------------------------------------ */
/* Primitive construction                                             */
/* ------------------------------------------------------------------ */

function buildPlaceholder(def: AssetDef): THREE.Object3D {
  const [w, h, d] = def.placeholder.size;
  const geometry = placeholderGeometry(def.placeholder.shape, w, h, d);

  // Origin at the base: shift the geometry up so y=0 is the resting surface.
  // This matches the importer's normalization, so swapping in a real GLB does
  // not move anything in the level.
  geometry.translate(0, h / 2, 0);

  const mesh = new THREE.Mesh(geometry, makeToonMaterial(def.placeholder.color));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.name = `${def.id}:placeholder`;
  return mesh;
}

function placeholderGeometry(
  shape: PlaceholderShape,
  w: number,
  h: number,
  d: number,
): THREE.BufferGeometry {
  switch (shape) {
    case 'box':
      return new THREE.BoxGeometry(w, h, d);

    case 'cylinder':
      return new THREE.CylinderGeometry(w / 2, w / 2, h, 12);

    case 'sphere': {
      const sphere = new THREE.SphereGeometry(0.5, 14, 10);
      sphere.scale(w, h, d);
      return sphere;
    }

    case 'cone':
      return new THREE.ConeGeometry(w / 2, h, 14);

    case 'capsule': {
      // A cat body reads far better as a horizontal lozenge than a standing
      // pill, so orient along the longest horizontal axis when depth wins.
      const radius = Math.min(w, h) / 2;
      if (d > h) {
        const capsule = new THREE.CapsuleGeometry(radius, Math.max(0.01, d - radius * 2), 6, 10);
        capsule.rotateX(Math.PI / 2);
        return capsule;
      }
      return new THREE.CapsuleGeometry(radius, Math.max(0.01, h - radius * 2), 6, 10);
    }
  }
}

function errorBox(): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(0.2, 0.2, 0.2),
    new THREE.MeshBasicMaterial({ color: 0xff00ff }),
  );
  mesh.name = 'asset:error';
  return mesh;
}

/** Test/teardown hook. */
export function __resetLoadedModels(): void {
  loadedModels.clear();
}

/** Test hook for the same validation and preparation path used by GLTFLoader. */
export function __registerLoadedModelForTests(id: string, root: THREE.Object3D): string[] {
  const def = defs.get(id);
  if (!def) return [`unknown asset "${id}"`];
  loadedModels.delete(id);
  return registerLoadedModel(def, root);
}

function registerLoadedModel(def: AssetDef, root: THREE.Object3D): string[] {
  const problems = def.rig ? validateAssetRig(root, def.rig) : [];
  if (problems.length > 0) return problems;

  applyToonShading(root);
  loadedModels.set(def.id, root);
  return [];
}
